import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Alert, Box } from '@mui/material';
import { getCustomerPortalCustomers } from '../../api/customerPortalApi';
import { DataTable } from '../../components/DataTable';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { StatusBadge } from '../../components/StatusBadge';
import { TableToolbar } from '../../components/TableToolbar';
import { IconButton } from '../../components/ui/IconButton';

export function CustomerPortalCustomersPage() {
  const { t } = useTranslation(['customers', 'common']);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const emptyValue = t('common:empty_value');
  const recordCount = customers.length;

  useEffect(() => {
    setLoading(true);
    setError('');
    getCustomerPortalCustomers()
      .then((response) => setCustomers(response.data || []))
      .catch(() => setError(t('customers:portal.load_failed')))
      .finally(() => setLoading(false));
  }, [t]);

  const columns = useMemo(() => [
    {
      id: 'name',
      label: t('common:name'),
      render: (row) => row.name || emptyValue,
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => (
        row.status
          ? <StatusBadge status={row.status} category="customer" size="Sm" />
          : emptyValue
      ),
    },
    {
      id: 'tenants_count',
      label: t('common:tenants'),
      render: (row) => (row.tenants_count != null ? row.tenants_count : emptyValue),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      width: 72,
      render: (row) => (
        <IconButton
          size="Sm"
          tone="Neutral"
          aria-label={t('customers:portal.open_detail')}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/portal/customers/${row.id}`);
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      ),
    },
  ], [emptyValue, navigate, t]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('customers:portal.title')}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ mb: 0, width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('customers:portal.companies')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('customers:portal.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton type="Table" embedded rows={4} columns={columns.length} />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={customers}
            getRowKey={(row) => row.id}
            emptyMessage={t('customers:portal.empty_list')}
            onRowClick={(row) => navigate(`/portal/customers/${row.id}`)}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>
    </Box>
  );
}
